import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";

import { CREATE_AD_V2_ROUTE } from "@/features/create-ad-v2/static/create-ad-v2.static";
import { useSellRoute } from "@/features/us-market/hooks/useSellRoute";
import { ValueCheckPhotoButton } from "@/features/value-check/components/ValueCheckPhotoButton";
import { ValueCheckPriceBlock } from "@/features/value-check/components/ValueCheckPriceBlock";
import { ValueCheckThumb } from "@/features/value-check/components/ValueCheckThumb";
import type { ValueCheckResult } from "@/features/value-check/model";
import {
  VALUE_CHECK_FROM_PARAM,
  VALUE_CHECK_FROM_VALUE,
} from "@/features/value-check/value-check.static";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";
import { Flavor } from "@/ui/typography/Flavor";
import { Helper } from "@/ui/typography/Helper";

const CREATE_AD_HREF = `${CREATE_AD_V2_ROUTE}?${VALUE_CHECK_FROM_PARAM}=${VALUE_CHECK_FROM_VALUE}`;

/**
 * The answer, in one centred column so the price is on the first screen of a phone (WHO-6534
 * F29): the page title, the photo as a small square beside what we saw, what comparable items
 * sold for (or that we have no price yet, or that we could not tell what it is), then "Create
 * ad". "Create ad" is always there; it asks for an account only now. The "for sale now" cards
 * are gone at Evelien's request (4 Nov).
 */
export const ValueCheckResultView = ({
  photoUrl,
  result,
  onCheckAnother,
  onPick,
}: {
  photoUrl: string;
  result: ValueCheckResult;
  onCheckAnother: () => void;
  /** A new photo straight from the "Try another photo" button. */
  onPick: (file: File) => void;
}) => {
  const { t } = useTranslation();
  const { href: createAdHref } = useSellRoute("value_check", CREATE_AD_HREF);
  const { labels } = result;
  const seen = [labels.kind, labels.brand, labels.material, labels.era].filter(
    (label): label is string => !!label
  );

  return (
    <section
      aria-labelledby="value-check-result-title"
      className="mx-auto grid max-w-[640px] gap-4 md:gap-6"
    >
      <Heading
        as="h1"
        size="h1"
        id="value-check-result-title"
        className="mb-2 md:mb-0"
      >
        {t("value-check:title", "What is your item worth?")}
      </Heading>

      <div className="flex items-start gap-4 md:gap-6">
        <ValueCheckThumb
          photoUrl={photoUrl}
          className="size-20 md:size-[120px]"
          data-testid="value-check.photo"
        />
        {result.recognised ? (
          <div className="grid min-w-0 gap-2" data-testid="value-check.seen">
            <div className="grid">
              <Flavor className="text-grey-700">
                {t("value-check:seen-title", "This is what we saw")}
              </Flavor>
              {result.title && (
                <Heading as="h2" size="h4">
                  {result.title}
                </Heading>
              )}
            </div>
            <ul
              aria-label={t("value-check:seen-labels", "Recognised")}
              className="flex flex-wrap gap-2"
            >
              {seen.map(label => (
                <li key={label}>
                  <InfoLabel color="curious" size="small" corners="square">
                    {label}
                  </InfoLabel>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div
            className="grid min-w-0 gap-1"
            data-testid="value-check.not-recognised"
          >
            <Heading as="h2" size="h4">
              {t(
                "value-check:not-recognised-title",
                "We don't recognise this item"
              )}
            </Heading>
            <Body className="leading-5 text-grey-800 md:leading-6">
              {t(
                "value-check:not-recognised-body",
                "Try a photo with the whole item in view, or create your ad and fill in the details yourself."
              )}
            </Body>
          </div>
        )}
      </div>

      {result.recognised && result.price && (
        <ValueCheckPriceBlock price={result.price} />
      )}

      {result.recognised && !result.price && (
        <div
          className="grid gap-1 rounded bg-flavour-50 p-4"
          data-testid="value-check.no-price"
        >
          <Heading as="h2" size="h5">
            {t(
              "value-check:no-price-title",
              "We don't have a price for this yet"
            )}
          </Heading>
          <Helper className="text-grey-800">
            {t(
              "value-check:no-price-body",
              "Too few comparable items have sold on Whoppah yet. You can still offer it and choose your own price."
            )}
          </Helper>
        </div>
      )}

      <div className="grid gap-2">
        {/* Nothing recognised: another photo is the way on, create-ad the fallback (F4). */}
        {!result.recognised && (
          <ValueCheckPhotoButton
            label={t("value-check:try-another-photo", "Try another photo")}
            onPick={onPick}
          />
        )}
        <LinkButton
          href={createAdHref}
          color="brand"
          appearance={result.recognised ? "solid" : "outlined"}
          size="medium"
          className={
            result.recognised
              ? "rounded border-0 text-center"
              : "rounded text-center"
          }
          data-testid="value-check.create-ad"
        >
          {t("value-check:create-ad", "Create ad")}
        </LinkButton>
        <Helper className="text-grey-700">
          {result.recognised
            ? t(
                "value-check:create-ad-note",
                "Your photo and what we recognised are already in it. You can still change everything."
              )
            : t(
                "value-check:create-ad-note-unrecognised",
                "Your photo is already in it. You fill in the details yourself."
              )}
        </Helper>
        {result.recognised && (
          <Button
            type="button"
            color="brand"
            appearance="minimal"
            size="medium"
            className="justify-self-start px-0 underline"
            onClick={onCheckAnother}
          >
            {t("value-check:check-another", "Check another item")}
          </Button>
        )}
      </div>
    </section>
  );
};
